import { useMemo } from 'react';

import { useGetVideo } from '@vimeo/gctl-api/hooks/useVideo';
import type { VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useViewer } from '@vimeo/viewer';

import { getVersionId } from '../utils/getVersionId';
import { mapVideoIdTypeForGCTL } from '../utils/mapVideoIdTypeForGCTL';

const CLIP_FIELDS: VideoFields[] = [
  'language',
  'usesDrm',
  'metadata.interactions.translations',
  'metadata.interactions.edit.uri',
  'metadata.connections.versions.currentUri',
  'metadata.connections.versions.latestIncompleteVersion',
  'user.uri',
];

type Props = {
  videoId?: string;
  disabled?: boolean;
};
export function useClipMetadata({ videoId, disabled }: Props) {
  const viewer = useViewer();

  const {
    data: videoMetadata,
    error: clipMetadataError,
    isLoading,
    isValidating,
  } = useGetVideo(() => {
    if (!videoId || disabled) return null;
    return {
      where: mapVideoIdTypeForGCTL(videoId),
      select: CLIP_FIELDS,
      headers: { Accept: 'application/vnd.vimeo.*+json;version=3.4.10' },
    };
  });

  const hasVideoTranslations = !!videoMetadata?.metadata?.interactions?.translations?.uri;

  const hasEditRights = !!videoMetadata?.metadata?.interactions?.edit?.uri;

  const currentVideoUri = videoMetadata?.metadata?.connections?.versions?.currentUri;

  const allVideoVersions = videoMetadata?.metadata?.connections?.versions ?? [];

  const versionId = useMemo(() => {
    return videoMetadata?.metadata?.connections?.versions?.currentUri
      ? Number(getVersionId(videoMetadata?.metadata.connections.versions.currentUri))
      : undefined;
  }, [videoMetadata?.metadata?.connections?.versions?.currentUri]);

  const ownerUri = videoMetadata?.user?.uri;
  const isOwner = Boolean(ownerUri && ownerUri === viewer?.user?.uri);

  return {
    hasVideoTranslations,
    mostRecentVersion: versionId && versionId >= 0 ? versionId : undefined,
    currentVideoUri,
    allVideoVersions,
    isFetching: isLoading || isValidating,
    videoLanguage: videoMetadata?.language,
    usesDrm: videoMetadata?.usesDrm,
    clipMetadataError,
    canEdit: isOwner || hasEditRights,
  };
}
